import { company } from "@purlink/shared/company";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarDays } from "lucide-react";
import { useEffect } from "react";
import { PublicLayout } from "~/components/public/public-layout";
import { Alert, Skeleton } from "~/components/ui/feedback";
import { EnquiryForm } from "~/features/enquiry/enquiry-form";
import {
  CallToActionBand,
  ENQUIRY_FORM_ID,
  HeroIntro,
  HowItWorks,
  IndustriesSection,
  OutcomesSection,
  TestimonialsSection,
  ToolsSection,
} from "~/features/enquiry/landing-sections";
import { api, unwrap } from "~/lib/api";
import { captureAttribution } from "~/lib/attribution";

export const Route = createFileRoute("/enquire/")({
  component: EnquirePage,
});

function EnquirePage() {
  const navigate = useNavigate();
  const config = useQuery({
    queryKey: ["public", "form-config"],
    queryFn: () => unwrap(api.api.public["form-config"].$get()),
    staleTime: 5 * 60_000,
  });

  // Record campaign tags as soon as someone lands, before any navigation.
  useEffect(() => {
    captureAttribution();
  }, []);

  return (
    <PublicLayout wide>
      <div className="flex flex-col gap-14 sm:gap-20">
        {/* Phones: pitch, form, then how it works. Desktop: form on the right, beside both. */}
        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,32rem)] lg:grid-rows-[auto_1fr] lg:gap-x-12">
          <HeroIntro />
          <section
            id={ENQUIRY_FORM_ID}
            aria-labelledby="enquiry-heading"
            className="surface-card scroll-mt-6 rounded-xl p-5 shadow-lg sm:p-8 lg:col-start-2 lg:row-span-2 lg:row-start-1"
          >
            <div className="mb-6 flex flex-col gap-2">
              <span className="mb-1 grid size-12 place-items-center rounded-full bg-primary-soft text-primary">
                <CalendarDays className="size-5" aria-hidden />
              </span>
              <p className="text-caption font-medium tracking-[0.16em] text-fg-muted uppercase">
                Free strategy call
              </p>
              <h2
                id="enquiry-heading"
                className="text-[1.5rem] leading-tight font-bold tracking-[-0.02em] text-fg"
              >
                See how we can streamline your business
              </h2>
              <p className="text-body text-fg-secondary">
                Tell us a bit about your business and we'll show you exactly how we can help.
              </p>
            </div>
            {config.isPending ? (
              <div className="flex flex-col gap-4" aria-busy>
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-28" />
              </div>
            ) : config.isError ? (
              <Alert tone="error" title="The enquiry form couldn't load.">
                Please refresh the page, or email us at {company.contactEmail}.
              </Alert>
            ) : (
              <EnquiryForm
                config={config.data}
                onSubmitted={() => void navigate({ to: "/enquire/thanks" })}
              />
            )}
          </section>
          <HowItWorks />
        </div>
        <IndustriesSection />
        <OutcomesSection />
        <TestimonialsSection />
        <CallToActionBand />
        <ToolsSection />
      </div>
    </PublicLayout>
  );
}
